import * as React from "react"
import AudioHandler from "../components/audioPlayer/audioHandler"
import Waveform from "../components/audioPlayer/waveform"
import Display from "../components/audioPlayer/display"
import styled from "styled-components"
import { getCalculationSize } from "../components/audioPlayer/waveformUtils"
import { parseTimestamp } from "../components/audioPlayer/audioHandlerUtils"

const Div = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100vw;
  height: 100px;
  background-color: black;
  z-index: 100;
  padding: 5px 25px;
`

export interface AudioMetadata {
  data: number[] | null
}

export interface TrackInfo {
  src: string
  trackList: Track[]
  playlistName: string
  playlistUrl: string
  metadataUrl: string
}

interface AudioPlayerContextType {
  isPlaying: boolean
  setIsPlaying: React.Dispatch<React.SetStateAction<boolean>>
  audioRef: React.RefObject<HTMLAudioElement>
  switchToTrack: (index: number, newTrackInfo?: TrackInfo) => void
  switchToRandomPost: () => void
  currentTrack: Track | null
  setTrackInfo: React.Dispatch<React.SetStateAction<TrackInfo | null>>
  trackInfo: TrackInfo | null
  setShouldAutoPlay: React.Dispatch<React.SetStateAction<boolean>>
  allPosts: TrackInfo[]
  setAllPosts: React.Dispatch<React.SetStateAction<TrackInfo[]>>
}

interface Track {
  title: string
  timestamp: string
}

// Helper function to play audio with error handling
const playAudio = (
  audioElement: HTMLAudioElement,
  setIsPlaying: React.Dispatch<React.SetStateAction<boolean>>
) => {
  audioElement
    .play()
    .then(() => {
      setIsPlaying(true)
    })
    .catch(error => {
      console.error("Error playing audio:", error)
    })
}

// Helper function to set track time and play if needed
const setTrackTimeAndPlay = (
  audioRef: React.RefObject<HTMLAudioElement>,
  time: number,
  isPlaying: boolean,
  setIsPlaying: React.Dispatch<React.SetStateAction<boolean>>
) => {
  if (audioRef.current) {
    audioRef.current.currentTime = time

    // Auto-play if not already playing
    if (!isPlaying && audioRef.current) {
      playAudio(audioRef.current, setIsPlaying)
    }
  }
}

const AudioPlayerContext = React.createContext<AudioPlayerContextType | null>(
  null
)

export const AudioPlayerProvider: React.FC<React.PropsWithChildren<{}>> = ({
  children,
}) => {
  const [isPlaying, setIsPlaying] = React.useState(false)
  const [currentTime, setCurrentTime] = React.useState(0)
  const [audioMetadata, setAudioMetadata] =
    React.useState<AudioMetadata | null>(null)
  const [currentTrack, setCurrentTrack] = React.useState<Track | null>(null)
  const [trackInfo, setTrackInfo] = React.useState<TrackInfo | null>(null)

  const canvasRef = React.useRef<HTMLCanvasElement>(null)
  const samplesRef = React.useRef<number[]>([])
  const audioRef = React.useRef<HTMLAudioElement>(null)

  const [pendingTrackIndex, setPendingTrackIndex] = React.useState<
    number | null
  >(null)
  const [shouldAutoPlay, setShouldAutoPlay] = React.useState(false)
  const [duration, setDuration] = React.useState<number | null>(null)
  const [allPosts, setAllPosts] = React.useState<TrackInfo[]>([])

  const handleClick = (e: React.MouseEvent<HTMLCanvasElement, MouseEvent>) => {
    if (!duration || !samplesRef.current || !canvasRef.current) return
    const rect = canvasRef.current.getBoundingClientRect()
    const x = e.clientX - rect.left
    const { segmentCount, segmentWidth, gap } = getCalculationSize(
      canvasRef.current
    )
    const segmentIndex = Math.floor(x / (segmentWidth + gap))
    const newTime = (segmentIndex / segmentCount) * duration
    if (audioRef.current) {
      audioRef.current.currentTime = newTime
      setCurrentTime(newTime)
    }
  }

  // Unified effect to handle track changes and auto-play
  React.useEffect(() => {
    if (!trackInfo || !audioRef.current) return

    // Handle pending track index (for track switching)
    if (pendingTrackIndex !== null) {
      const track = trackInfo.trackList[pendingTrackIndex]
      const time = parseTimestamp(track.timestamp)
      setTrackTimeAndPlay(audioRef, time, isPlaying, setIsPlaying)
      setPendingTrackIndex(null)
      return
    }

    // Handle new track loading with auto-play
    if (shouldAutoPlay) {
      // Start playing from the beginning
      setTrackTimeAndPlay(audioRef, 0, isPlaying, setIsPlaying)
      setShouldAutoPlay(false)
    }
  }, [trackInfo, pendingTrackIndex, isPlaying, setIsPlaying, shouldAutoPlay])

  const switchToTrack = (index: number, newTrackInfo?: TrackInfo) => {
    // If we have a new track info, update the track info first
    if (newTrackInfo && (!trackInfo || trackInfo.src !== newTrackInfo.src)) {
      setTrackInfo(newTrackInfo)
      setPendingTrackIndex(index)
      return
    }

    // Switching tracks within the same playlist
    if (!trackInfo) return

    const track = trackInfo.trackList[index]
    const time = parseTimestamp(track.timestamp)
    setTrackTimeAndPlay(audioRef, time, isPlaying, setIsPlaying)
  }

  const switchToRandomPost = () => {
    if (allPosts.length === 0) return
    
    // Exclude current post
    const otherPosts = allPosts.filter(post => 
      post.playlistUrl !== trackInfo?.playlistUrl
    )
    
    if (otherPosts.length === 0) return
    
    const randomIndex = Math.floor(Math.random() * otherPosts.length)
    const randomPost = otherPosts[randomIndex]
    
    // Set the new trackInfo to play the random post
    setTrackInfo({
      ...randomPost,
      trackList: randomPost.trackList || []
    })
    setShouldAutoPlay(true)
  }

  return (
    <AudioPlayerContext.Provider
      value={{
        audioRef,
        isPlaying,
        setIsPlaying,
        switchToTrack,
        switchToRandomPost,
        currentTrack,
        setTrackInfo,
        trackInfo,
        setShouldAutoPlay,
        allPosts,
        setAllPosts
      }}
    >
      {children}

      {trackInfo && (
        <Div>
          <AudioHandler
            setCurrentTime={setCurrentTime}
            currentTime={currentTime}
            onTrackChange={setCurrentTrack}
            audioMetadata={audioMetadata}
            setAudioMetadata={setAudioMetadata}
            audioRef={audioRef}
            duration={duration}
            setDuration={setDuration}
          />
          <Waveform
            currentTime={currentTime}
            audioMetaData={audioMetadata}
            canvasRef={canvasRef}
            samplesRef={samplesRef}
            onClick={handleClick}
            duration={duration}
          />
          <Display
            currentTrack={currentTrack}
            currentTime={currentTime}
            duration={duration}
            trackInfo={trackInfo}
          />
        </Div>
      )}
    </AudioPlayerContext.Provider>
  )
}

export default AudioPlayerContext
